<template>
  <div class="user-table-container">
    <!-- Filtros -->
    <div class="filters mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="form-field">
        <label for="colegio-select" class="block text-sm font-medium text-gray-700">Colegio</label>
        <ColegioDropdown
          v-model="internalFilters.colegio"
          @change="handleFilterChange"
          :mostrarErrores="mostrarErrores"
          :tooltip="'Selecciona un Colegio'"
          :error="errors.colegio"
        />
        <small v-if="mostrarErrores && errors.colegio" class="p-error">{{ errors.colegio }}</small>
      </div>
      <div class="form-field" v-if="!listarTodoColegio">
        <label for="curso-select" class="block text-sm font-medium text-gray-700">Curso</label>
        <SingleCursoCombo
          :mostrarErrores="mostrarErrores"
          :tooltip="'Selecciona un Curso'"
          :error="errors.curso"
          :modelValue="internalFilters.curso"
          @update:modelValue="internalFilters.curso = $event; handleFilterChange()"
        />
        <small v-if="mostrarErrores && errors.curso" class="p-error">{{ errors.curso }}</small>
      </div>
      <div v-if="permitirListarTodoColegio" class="form-field flex items-center gap-2">
        <CheckboxPrime v-model="listarTodoColegio" :binary="true" inputId="listar-todo-colegio" @change="onTodoColegioToggle" />
        <label for="listar-todo-colegio" class="text-sm text-gray-700 cursor-pointer">Listar todos los cursos del colegio</label>
      </div>
    </div>

    <!-- Tabla -->
    <DataTable
      :value="usuarios"
      :paginator="true"
      :rows="rows"
      :totalRecords="totalRecords"
      :lazy="true"
      :filters="filters"
      :loading="loading"
      @page="onPage"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
      :rowsPerPageOptions="[20, 50, 100, 500, 800, 999]"
      currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} alumnos"
      responsiveLayout="scroll"
      class="p-datatable-sm"
      selectionMode="multiple"
      v-model:selection="selectedUsuarios"
      dataKey="id_usuario"
    >
      <ColumnPrime selectionMode="multiple" headerStyle="width: 3em" />
      <ColumnPrime field="NombreCompleto" header="Nombre Completo">
        <template #body="{ data }">
          <span>{{ data.Nombre }} {{ data.ApellidoPaterno }} {{ data.ApellidoMaterno }}</span>
        </template>
      </ColumnPrime>
      <ColumnPrime field="IdentificadorRut" header="RUT" />
      <ColumnPrime field="Email" header="Email" />
      <ColumnPrime v-if="mostrarColumnaCurso" field="curso_nombre" header="Curso" />
      <ColumnPrime header="Acciones">
        <template #body="slotProps">
          <ButtonPrime
            icon="pi pi-pencil"
            class="p-button-rounded p-button-success p-button-sm p-mr-2"
            @click="handleEditarUsuario(slotProps.data)"
          />
          <ButtonPrime
            icon="pi pi-copy"
            class="p-button-rounded p-button-success p-button-sm p-mr-2"
            @click="handleClonarUsuario(slotProps.data)"
          />
          <ButtonPrime
            icon="pi pi-book"
            class="p-button-rounded p-button-info p-button-sm p-mr-2"
            @click="handleModificarCurso(slotProps.data)"
          />
          <ButtonPrime
            icon="pi pi-list"
            class="p-button-rounded p-button-help p-button-sm p-mr-2"
            v-tooltip="'Asignar asignaturas al contrato'"
            @click="handleAsignarAsignaturas(slotProps.data)"
          />
        </template>
      </ColumnPrime>
    </DataTable>
  </div>
</template>

<script>
import DataTable from 'primevue/datatable';
import ColumnPrime from 'primevue/column';
import ButtonPrime from 'primevue/button';
import CheckboxPrime from 'primevue/checkbox';
import ColegioDropdown from '@/components/selectores/ColegioDropdown.vue';
import SingleCursoCombo from '@/components/selectores/SingleCursoCombo.vue';

export default {
  components: {
    DataTable,
    ColumnPrime,
    ButtonPrime,
    CheckboxPrime,
    ColegioDropdown,
    SingleCursoCombo
  },
  props: {
    usuarios: {
      type: Array,
      default: () => []
    },
    rows: Number,
    totalRecords: Number,
    filters: Object,
    loading: Boolean,
    permitirListarTodoColegio: {
      type: Boolean,
      default: false
    },
    mostrarColumnaCurso: {
      type: Boolean,
      default: false
    }
  },
  emits: ['clonarUsuario', 'editarUsuario', 'modificarCurso', 'asignarAsignaturas', 'update:filters', 'page', 'update:selectedUsuarios'],
  data() {
    return {
      listarTodoColegio: false,
      internalFilters: {
        colegio: '',
        curso: ''
      },
      errors: {
        colegio: '',
        curso: ''
      },
      mostrarErrores: false,
      selectedUsuarios: []
    };
  },
  watch: {
    filters: {
      handler(newFilters) {
        this.internalFilters = { ...newFilters };
        if (this.permitirListarTodoColegio && (newFilters.curso === 0 || newFilters.curso === '0')) {
          this.listarTodoColegio = true;
        }
      },
      deep: true,
      immediate: true
    },
    selectedUsuarios(newSelection) {
      this.$emit('update:selectedUsuarios', newSelection);
    }
  },
  methods: {
    onTodoColegioToggle() {
      if (this.listarTodoColegio) {
        this.internalFilters.curso = 0;
      } else {
        this.internalFilters.curso = '';
      }
      this.handleFilterChange();
    },
    handleFilterChange() {
      this.mostrarErrores = false;
      if (!this.internalFilters.colegio) {
        this.errors.colegio = 'Debes seleccionar un colegio';
        this.mostrarErrores = true;
      }
      const cursoRequerido = !this.permitirListarTodoColegio || !this.listarTodoColegio;
      if (cursoRequerido) {
        const c = this.internalFilters.curso;
        const vacio = c === '' || c === null || c === undefined || (Array.isArray(c) && c.length === 0);
        if (vacio) {
          this.errors.curso = 'Debes seleccionar un curso';
          this.mostrarErrores = true;
        }
      }
      if (this.mostrarErrores) return;

      const cursoEmit = this.listarTodoColegio && this.permitirListarTodoColegio ? 0 : this.internalFilters.curso;
      this.$emit('update:filters', { ...this.internalFilters, curso: cursoEmit });
    },
    onPage(event) {
      this.$emit('page', event);
    },
    handleClonarUsuario(usuario) {
      this.$emit('clonarUsuario', usuario);
    },
    handleEditarUsuario(usuario) {
      this.$emit('editarUsuario', usuario);
    },
    handleModificarCurso(usuario) {
      this.$emit('modificarCurso', usuario);
    },
    handleAsignarAsignaturas(usuario) {
      this.$emit('asignarAsignaturas', usuario);
    }
  }
};
</script>

<style scoped>
.user-table-container {
  @apply p-4;
}

.form-field {
  @apply mb-4;
}

.form-field label {
  @apply block text-sm font-medium text-gray-700 mb-1;
}
</style>